/**
 * CategoryLauncherScreen Component
 * 
 * Split-panel launcher for category configuration
 * - Left: Discovery Questions (wizard)
 * - Right: Request Type Builder
 * - Shows completion status, question counts, time estimates
 * - Handles Incident, Change, Problem, and Service Request categories
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import { 
  HelpCircle, 
  FileText, 
  ChevronRight, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  ArrowLeft,
  Sparkles,
  Plus
} from 'lucide-react';
import { DynamicWizard } from './DynamicWizard';
import { RequestTypeFormBuilder } from './RequestTypeFormBuilder';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface CategoryLauncherScreenProps {
  category: 'incident_management' | 'change_management' | 'problem_management' | 'service_request_management';
  categoryDisplayName: string;
  configItemId: string;
  companyId: string;
  sowId?: string;
  consultingFirmId: string;
  userRole: 'client' | 'consultant';
  onBack: () => void;
  categoryStatus?: 'not-started' | 'in-progress' | 'ready-for-implementation' | 'in-review' | 'done';
}

interface WizardInfo {
  id: string;
  name: string;
  totalQuestions: number;
  answeredQuestions: number;
  estimatedMinutes: number;
  isComplete: boolean;
}

interface RequestTypeInfo {
  id: string;
  name: string;
  status: 'draft' | 'complete';
  fieldCount: number;
}

export function CategoryLauncherScreen({
  category,
  categoryDisplayName,
  configItemId,
  companyId,
  sowId,
  consultingFirmId,
  userRole,
  onBack,
  categoryStatus = 'not-started'
}: CategoryLauncherScreenProps) {
  const [wizardInfo, setWizardInfo] = useState<WizardInfo | null>(null);
  const [requestTypes, setRequestTypes] = useState<RequestTypeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showRequestBuilder, setShowRequestBuilder] = useState(false);
  const [selectedRequestType, setSelectedRequestType] = useState<RequestTypeInfo | null>(null);

  // Load wizard and request type data
  useEffect(() => {
    loadCategoryData();
  }, [category, configItemId]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      
      console.log('[CategoryLauncherScreen] Loading data with params:', {
        category,
        configItemId,
        companyId
      });
      
      // Load discovery wizard info
      const wizardResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-bank/category-wizard-info`,
        {
          method: 'POST',
          headers: {
            ...bearerAuthHeader(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            category,
            configItemId,
            companyId
          })
        }
      );

      if (wizardResponse.ok) {
        const wizardData = await wizardResponse.json();
        console.log('[CategoryLauncherScreen] Wizard data loaded:', wizardData);
        setWizardInfo(wizardData.wizard);
      } else {
        const errorText = await wizardResponse.text();
        console.error('[CategoryLauncherScreen] Wizard API error:', {
          status: wizardResponse.status,
          error: errorText
        });
        
        if (wizardResponse.status === 400 || wizardResponse.status === 404) {
          // Route doesn't exist yet or not configured - use mock data
          console.log('[CategoryLauncherScreen] Wizard info API not available, using defaults');
          setWizardInfo({
            id: `wizard-${category}`,
            name: `${categoryDisplayName} Discovery`,
            totalQuestions: 0,
            answeredQuestions: 0,
            estimatedMinutes: 0,
            isComplete: false
          });
        }
      }

      // Load request types for this category
      const requestTypesResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/request-types/list`,
        {
          method: 'POST',
          headers: {
            ...bearerAuthHeader(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            categoryId: configItemId,
            companyId
          })
        }
      );

      if (requestTypesResponse.ok) {
        const requestTypesData = await requestTypesResponse.json();
        setRequestTypes(requestTypesData.requestTypes || []);
      } else if (requestTypesResponse.status === 400 || requestTypesResponse.status === 404) {
        // Route doesn't exist yet or not configured - use empty array
        console.log('[CategoryLauncherScreen] Request types API not available, using defaults');
        setRequestTypes([]);
      }

    } catch (error) {
      console.error('Error loading category data:', error);
      // Don't show error toast - gracefully degrade to empty state
      setWizardInfo({
        id: `wizard-${category}`,
        name: `${categoryDisplayName} Discovery`,
        totalQuestions: 0,
        answeredQuestions: 0,
        estimatedMinutes: 0,
        isComplete: false
      });
      setRequestTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const getTotalProgress = () => {
    if (!wizardInfo) return 0;
    
    const wizardProgress = wizardInfo.totalQuestions > 0 
      ? (wizardInfo.answeredQuestions / wizardInfo.totalQuestions) * 50 
      : 0;
    
    const requestTypeProgress = requestTypes.length > 0
      ? (requestTypes.filter(rt => rt.status === 'complete').length / requestTypes.length) * 50
      : 0;

    return Math.round(wizardProgress + requestTypeProgress);
  };

  const getTotalQuestions = () => {
    const wizardQuestions = wizardInfo?.totalQuestions || 0;
    const requestTypeQuestions = requestTypes.reduce((sum, rt) => sum + rt.fieldCount, 0);
    return wizardQuestions + requestTypeQuestions;
  };

  const getEstimatedTime = () => {
    const wizardTime = wizardInfo?.estimatedMinutes || 0;
    const requestTypeTime = requestTypes.length * 10; // Estimate 10 min per request type
    return wizardTime + requestTypeTime;
  };

  const isReadyForBuild = categoryStatus === 'ready-for-implementation' || 
                          categoryStatus === 'in-review' || 
                          categoryStatus === 'done';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Clock className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading category information...</p>
        </div>
      </div>
    );
  }

  if (showWizard && wizardInfo) {
    return (
      <DynamicWizard
        open={showWizard}
        onClose={() => {
          setShowWizard(false);
          loadCategoryData(); // Refresh data when wizard closes
        }}
        wizardId={wizardInfo.id}
        configItemId={configItemId}
        companyId={companyId}
        sowId={sowId}
        consultingFirmId={consultingFirmId}
        userRole={userRole}
        readyForBuildWarning={isReadyForBuild}
        onComplete={() => {
          setShowWizard(false);
          loadCategoryData();
        }}
      />
    );
  }

  if (showRequestBuilder) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => {
                setShowRequestBuilder(false);
                setSelectedRequestType(null);
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Category
            </Button>
          </div>
          
          <RequestTypeFormBuilder
            serviceProjectId={configItemId}
            requestType={selectedRequestType}
            companyId={companyId}
            sowId={sowId}
            consultingFirmId={consultingFirmId}
            onSave={() => {
              setShowRequestBuilder(false);
              setSelectedRequestType(null);
              loadCategoryData();
              toast.success('Request type saved successfully');
            }}
            onSaveAndAddAnother={() => {
              setSelectedRequestType(null);
              loadCategoryData();
              toast.success('Request type saved. Add another.');
            }}
            onCancel={() => {
              setShowRequestBuilder(false);
              setSelectedRequestType(null);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <h1 className="text-2xl font-semibold">{categoryDisplayName}</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Complete discovery questions and define request forms
            </p>
          </div>
          
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Overall Progress</div>
            <div className="text-2xl font-semibold">{getTotalProgress()}%</div>
            <Progress value={getTotalProgress()} className="w-32 mt-1" />
          </div>
        </div>

        {/* Status Warning */}
        {isReadyForBuild && (
          <Alert className="border-orange-200 bg-orange-50">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <strong>This category is marked as "{categoryStatus?.replace(/-/g, ' ')}".</strong> 
              Any changes made now may require rework and could trigger a Change Order. 
              Proceed with caution.
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-semibold">{getTotalQuestions()}</div>
              <p className="text-xs text-muted-foreground">Total Configuration Items</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-semibold">~{getEstimatedTime()} min</div>
              <p className="text-xs text-muted-foreground">Estimated Time Remaining</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-semibold">{requestTypes.length}</div>
              <p className="text-xs text-muted-foreground">Request Types Defined</p>
            </CardContent>
          </Card>
        </div>

        {/* Split Panel - Discovery & Request Forms */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT PANEL - Discovery Questions */}
          <Card className="border-2 hover:border-primary/50 transition-colors">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <HelpCircle className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>{categoryDisplayName} Questions</CardTitle>
                    <CardDescription className="mt-1">
                      Answer discovery questions to define requirements
                    </CardDescription>
                  </div>
                </div>
                {wizardInfo?.isComplete && (
                  <Badge variant="default" className="bg-green-600">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Complete
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Progress */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">
                    {wizardInfo?.answeredQuestions || 0} / {wizardInfo?.totalQuestions || 0} questions
                  </span>
                </div>
                <Progress 
                  value={wizardInfo?.totalQuestions ? (wizardInfo.answeredQuestions / wizardInfo.totalQuestions) * 100 : 0} 
                />
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">~{wizardInfo?.estimatedMinutes || 0} minutes</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">AI-assisted</span>
                </div>
              </div>

              {/* CTA */}
              <Button 
                className="w-full mt-4" 
                size="lg"
                onClick={() => setShowWizard(true)}
              >
                {wizardInfo?.answeredQuestions ? 'Continue Questions' : 'Start Discovery'}
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>

              {wizardInfo?.isComplete && (
                <p className="text-xs text-center text-muted-foreground">
                  All questions answered. You can review or make changes.
                </p>
              )}
            </CardContent>
          </Card>

          {/* RIGHT PANEL - Request Type Builder */}
          <Card className="border-2 hover:border-primary/50 transition-colors">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FileText className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle>Define Request Forms</CardTitle>
                    <CardDescription className="mt-1">
                      Build forms for {categoryDisplayName.toLowerCase()} submission
                    </CardDescription>
                  </div>
                </div>
                {requestTypes.length > 0 && requestTypes.every(rt => rt.status === 'complete') && (
                  <Badge variant="default" className="bg-green-600">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Complete
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Request Types List */}
              {requestTypes.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium mb-2">Defined Request Types</div>
                  {requestTypes.map((rt) => (
                    <div
                      key={rt.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer"
                      onClick={() => {
                        setSelectedRequestType(rt);
                        setShowRequestBuilder(true);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{rt.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {rt.fieldCount} fields
                        </Badge>
                      </div>
                      <Badge variant={rt.status === 'complete' ? 'default' : 'secondary'}>
                        {rt.status === 'complete' ? 'Complete' : 'Draft'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">No request types defined yet</p>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">~10 min each</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{requestTypes.length} forms</span>
                </div>
              </div>

              {/* CTA */}
              <Button 
                className="w-full mt-4" 
                size="lg"
                variant={requestTypes.length > 0 ? "outline" : "default"}
                onClick={() => {
                  setSelectedRequestType(null);
                  setShowRequestBuilder(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {requestTypes.length > 0 ? 'Add Another Request Type' : 'Create First Request Type'}
              </Button>

              {requestTypes.length > 0 && (
                <p className="text-xs text-center text-muted-foreground">
                  Click a request type above to edit it
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Completion Reminder */}
        {getTotalProgress() < 100 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Remember:</strong> Both discovery questions AND request type forms must be completed 
              before marking this category as "Ready for Build".
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}